import React, {
  FunctionComponent,
  SyntheticEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Container, Grid } from 'semantic-ui-react';
import { useMutation, useQuery } from '@apollo/react-hooks';
import withStyles from 'react-jss';
import classnames from 'classnames';
import { omit, pathOr } from 'ramda';
import { format } from 'date-fns';
import { useParams } from 'react-router-dom';

import { ClassProp } from 'helpers/types';
import { useGetMarketplaces } from 'helpers/useGetMarketplaces';
import FormSelectList from 'components/FormComponents/FormSelect';
import FormDateInput from 'components/FormComponents/FormDateInput';
import FormInput from 'components/FormComponents/FormInput';
import FormTimeInput from 'components/FormComponents/FormTimeInput';
import Button from 'components/Button';
import { GET_PROFILE } from 'components/UserMgmt/UserMgmt.queries';
import Slider from 'components/FormComponents/Slider';
import {
  FinancialTermUnitTypes,
  SegmentTypes,
  getMarketingFee,
  ThrottleOptions,
  buildTargetingRules,
  handleSuccess,
  handleError,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffer.helpers';
import {
  buildTimeStamp,
  cstToUTC,
  dateIsAfterCurrentDate,
  DATE_FORMAT,
  checkDates,
  midnight,
  oneMinuteToMidnight,
  getCurrentCSTDateWithTime,
} from 'helpers/date';
import {
  Segment,
  TermUnits,
  SegmentType,
  PromotionType,
  AddSegmentFormData,
  FinancialTermItem,
  TermType,
  Throttle,
  PayerType,
  TargetingRuleType,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffer.types';
import { Profile } from 'components/UserMgmt/UserMgmt.helpers';
import { toastMapping } from 'components/ErrorBoundary/ErrorBoundary';
import { DropDown } from 'components/FormSelect/FormSelect.helpers';
import {
  ADD_OFFER_SEGMENT,
  GET_OFFER,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/scenes/PulseOffer/PulseOffer.queries';

import SelectAudience, {
  AudienceSelectEvent,
  AttachedAudiences,
} from '../../../SelectAudience';

import styles from './NewOfferSegment.styles';

type Styles = ClassProp<ReturnType<typeof styles>>;

type Props = {
  classes: Styles;
  onCancel: () => void;
  onSuccess: () => void;
  insertionOrderRef: string;
  attachedThrottles: Throttle[];
  attachedAudiences: AttachedAudiences;
  baseSegment: Segment;
};
interface UserProfileData {
  userProfile: Profile;
}

const defaultSuccessMessage = [
  {
    displayMessage: 'Success!',
    message: 'Offer Segment Created.',
  },
];

const AddSegment: FunctionComponent<Props> = ({
  onCancel,
  onSuccess,
  classes,
  baseSegment,
  insertionOrderRef,
  attachedThrottles,
  attachedAudiences,
}) => {
  const [payerType, setPayerType] = useState<PayerType>();
  const [marketingFee, setMarketingFee] = useState<string>();
  const [budgetId, setBudgetId] = useState<string>();
  const financialTermItemsByTermType = useRef<Map<string, string>>(new Map());
  useEffect(() => {
    const defaultFinancialTermItems = pathOr(
      null,
      ['financialTerm', 'financialTermItems'],
      baseSegment,
    ) as FinancialTermItem[] | null;
    if (defaultFinancialTermItems) {
      const { budgetId: defaultBudgetId } = defaultFinancialTermItems[0];
      setBudgetId(defaultBudgetId);
      const defaultPayerType = defaultFinancialTermItems.find(
        (termItem) => termItem.termType === TermType.CASHBACK,
      )?.payerType;
      setPayerType(defaultPayerType);
      defaultFinancialTermItems.reduce(
        (acc, financialTerm) =>
          acc.set(financialTerm.termType, financialTerm.budgetId),
        financialTermItemsByTermType.current,
      );
      const defaultMarketingFee = getMarketingFee(defaultFinancialTermItems);
      setMarketingFee(defaultMarketingFee);
    } else {
      toastMapping(
        {},
        'error',
      )('No Financial Term Items were found on the Base Segment' as any);
    }
  }, []);

  const initialThrottleOptions: DropDown = {
    options: [{ key: 'select', value: null, text: 'Select' }],
    label: {
      children: 'Throttle',
    },
    placeholder: 'Select Throttle',
  };

  const { MarketplaceOptions, buildMarketplaces } = useGetMarketplaces();

  const [usePercentages, setUsePercentages] = useState<boolean>(
    !baseSegment.cashbackAmount,
  );
  const [userName, setUserName] = useState<string | null>(null);
  const [useThrottle, setThrottle] = useState<string | null>(null);
  const [throttleId, setThrottleId] = useState<string | null>(null);
  const [segmentType, setSegmentType] = useState<SegmentType>(
    SegmentType.TARGETED,
  );
  const [audience, setAudience] = useState<string | null>(null);
  const [
    existingThrottleOptions,
    setExistingThrottleOptions,
  ] = useState<DropDown>(initialThrottleOptions);
  const { merchantId = '', offerUuid = '' } = useParams<any>();

  useEffect(() => {
    attachedThrottles.forEach((throttle) => {
      if (
        throttle &&
        !existingThrottleOptions.options.some(
          (option) => option.key === throttle.uuid,
        )
      ) {
        existingThrottleOptions.options.push({
          key: throttle.uuid,
          value: throttle.uuid,
          text: throttle.name,
        });
      }
    });

    setExistingThrottleOptions(existingThrottleOptions);
  }, [attachedThrottles]);

  const defaultStartDate = dateIsAfterCurrentDate(baseSegment.startDate)
    ? new Date(baseSegment.startDate)
    : new Date();
  const defaultEndDate = new Date(baseSegment.endDate || '12/31/2035');

  const methods = useForm<AddSegmentFormData>({
    mode: 'onBlur',
    defaultValues: {
      offerTitleOverride: baseSegment.offerTitleOverride,
      hideOfferForSegment: baseSegment.hideOfferForSegment,
      cashbackDailyLimit: baseSegment.cashbackDailyLimit,
      marketplaces: baseSegment.marketplaces,
      cashback: baseSegment.cashbackAmount || baseSegment.cashbackPercentage,
      marketingFee,
      startDate: defaultStartDate,
      startTime: format(midnight(), 'HH:mm'),
      endDate: defaultEndDate,
      endTime: format(oneMinuteToMidnight(), 'HH:mm'),
    },
  });

  const {
    register,
    handleSubmit,
    getValues,
    watch,
    setValue,
    formState,
    trigger,
  } = methods;

  useEffect(() => {
    register({ name: 'financialTermUnit' });
    register({ name: 'marketplaces' });
    register({ name: 'segmentType' });
    register(
      { name: 'startDate' },
      {
        required: 'Start Date is required',
        validate: {
          oneOf: () =>
            checkDates(getValues().startDate, getValues().endDate) ||
            'Start Date must come before End Date',
          isAfter: (value) =>
            dateIsAfterCurrentDate(
              getCurrentCSTDateWithTime(value, getValues().startTime),
            ) || "Start Date can't be earlier than today",
        },
      },
    );
    register(
      { name: 'endDate' },
      {
        required: 'End Date is required',
        validate: {
          oneOf: () =>
            checkDates(getValues().startDate, getValues().endDate) ||
            'End Date must come after Start Date',
          isAfter: (value) =>
            dateIsAfterCurrentDate(value) ||
            "End Date can't be earlier than today",
        },
      },
    );
    register({ name: 'startTime' }, { required: 'Start Time is required' });
    register({ name: 'endTime' }, { required: 'End Time is required' });
  }, [register]);

  const hideOfferForSegment = watch('hideOfferForSegment');

  const selectedStartDate = watch('startDate');
  const selectedEndDate = watch('endDate');
  const selectedStartTime = watch('startTime');

  useEffect(() => {
    trigger('endDate');
  }, [selectedStartDate]);

  useEffect(() => {
    trigger('startDate');
  }, [selectedEndDate, selectedStartTime]);

  const handleAddSegmentResponse = () => {
    handleSuccess(defaultSuccessMessage);
    onSuccess();
  };

  const [runMutation, { loading: isSubmitting }] = useMutation(
    ADD_OFFER_SEGMENT,
    {
      onCompleted: handleAddSegmentResponse,
      onError: handleError,
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GET_OFFER,
          variables: {
            params: {
              offerUuid,
              hydrateFinancialTerms: true,
            },
          },
        },
      ],
    },
  );

  const { data, loading: profileLoading } = useQuery<UserProfileData>(
    GET_PROFILE,
  );

  const handleUseThrottleChange = (throttle: string) => {
    setThrottle(throttle);
  };

  const handleThrottleChange = (throttleId: string) => {
    setThrottleId(throttleId);
  };

  const handleSegmentTypeChange = (segmentType: SegmentType) => {
    if (segmentType === SegmentType.MUG) {
      setValue('audience', null);
      setAudience(null);
    }
    setSegmentType(segmentType);
  };

  const handleAudienceChange = ({
    audienceId,
    defaultPriority,
  }: AudienceSelectEvent) => {
    setValue('segmentPriority', defaultPriority);
    setAudience(audienceId);
  };

  useEffect(() => {
    if (profileLoading || !data) return;
    const {
      userProfile: { givenName, familyName },
    } = data;
    setUserName(`${givenName} ${familyName}`);
  }, [data, profileLoading]);

  useEffect(() => {
    if (segmentType && segmentType === SegmentType.MUG) {
      setValue('segmentPriority', 150);
    }
  }, [segmentType]);

  const handleClick = (_: SyntheticEvent<HTMLElement, Event>) => onCancel();

  const changeFinancialUnit = (termUnit: TermUnits) => {
    setUsePercentages(termUnit === TermUnits.PERCENTAGE);
  };

  const currentMax = watch('max');
  const currentMin = watch('min');

  const throttleRange = (max: number, min: number) => (max - min + 1) / 10;

  const onSubmit = ({
    startDate,
    startTime,
    endDate,
    endTime,
    cashback,
    baseCashback,
    cashbackDailyLimit,
    marketingFee,
    marketplaces,
    offerTitleOverride,
    segmentPriority,
    useThrottle,
    mugId,
    ...data
  }: AddSegmentFormData) => {
    const cashbackNumber = parseFloat(cashback as string);
    const marketingFeeNumber = parseFloat(marketingFee as string);

    const offerThrottleV1Rule = baseSegment.targetingRules.filter(
      (rule) =>
        rule.type === TargetingRuleType.OFFER_THROTTLE_V1 &&
        rule.data.includes(throttleId ?? ''),
    )[0];

    const financialTermItems = [
      {
        termType: 'CAMPAIGN_FEE',
        payerType,
        termValue: cashbackNumber + marketingFeeNumber,
        termUnits: usePercentages ? 'PERCENTAGE' : 'USD',
        budgetId:
          financialTermItemsByTermType.current.get('CAMPAIGN_FEE') ?? budgetId,
      },
      {
        termType: 'MARKETING_FEE',
        payerType,
        termValue: marketingFeeNumber,
        termUnits: usePercentages ? 'PERCENTAGE' : 'USD',
        budgetId:
          financialTermItemsByTermType.current.get('MARKETING_FEE') ?? budgetId,
      },
      {
        termType: 'CASHBACK',
        payerType,
        termValue: cashbackNumber,
        termUnits: usePercentages ? 'PERCENTAGE' : 'USD',
        budgetId:
          financialTermItemsByTermType.current.get('CASHBACK') ?? budgetId,
      },
    ];

    const startTimeStamp = buildTimeStamp(
      format(startDate, DATE_FORMAT),
      startTime,
    );
    const endTimeStamp = buildTimeStamp(format(endDate, DATE_FORMAT), endTime);

    const targetingRules = buildTargetingRules(
      mugId,
      audience,
      throttleId,
      merchantId,
      segmentType,
      offerThrottleV1Rule,
    );

    const formattedSegment = {
      ...data,
      targetingRules,
      insertionOrderRef,
      segmentType,
      offerTitleOverride,
      financialTerm: {
        financialTermItems,
      },
      marketplaces: buildMarketplaces(marketplaces),
      startDate: cstToUTC(startTimeStamp),
      endDate: cstToUTC(endTimeStamp),
      cashbackPercentage: usePercentages ? cashbackNumber : null,
      cashbackAmount: usePercentages ? null : cashbackNumber,
      cashbackDailyLimit: parseFloat(cashbackDailyLimit as string),
      updatedBy: userName,
      dryRun: false,
      overrideCashbackCheck: false,
      segmentPriority: parseInt(segmentPriority as string, 10),
      promotionType: PromotionType.STANDARD,
    };

    const omitParams = [
      usePercentages ? 'cashbackAmount' : 'cashbackPercentage',
    ];
    if (offerTitleOverride.length < 1) omitParams.push('offerTitleOverride');

    const addSegment = omit(omitParams, formattedSegment);

    runMutation({
      variables: {
        params: { ...addSegment },
      },
    });
  };

  return (
    <Container className={classes.container}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <input
            type="hidden"
            value={offerUuid}
            name="offerUuid"
            ref={register}
          />
          <input
            type="hidden"
            value={insertionOrderRef}
            name="insertionOrderRef"
            ref={register}
          />
          <Grid>
            <Grid.Row className={classes.sectionHeader}>
              <h3>Create offer segment</h3>
            </Grid.Row>

            <Grid.Row>
              <Grid.Column width={4}>
                <FormDateInput
                  name="startDate"
                  label="Start Date"
                  placeholder={defaultStartDate}
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <FormTimeInput
                  name="startTime"
                  label="Start Time (CST)"
                  useCST={false}
                  placeholder={midnight()}
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <FormDateInput
                  name="endDate"
                  label="End Date"
                  placeholder={defaultEndDate}
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <FormTimeInput
                  name="endTime"
                  useCST={false}
                  label="End Time (CST)"
                  placeholder={oneMinuteToMidnight()}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={16}>
                <FormInput
                  name="offerTitleOverride"
                  type="text"
                  placeholder="Enter an Offer Title Override"
                  label="Offer Title Override"
                  register={register}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={4}>
                <FormInput
                  name="hideOfferForSegment"
                  type="checkbox"
                  placeholder="Hide Offer For Segment"
                  label="Hide Offer For Segment"
                  register={register}
                />
              </Grid.Column>
            </Grid.Row>

            <Grid.Row className={classes.sectionHeader}>
              <hr className={classes.divider} />
              <h3>Financial Terms</h3>
            </Grid.Row>

            <Grid.Row>
              <Grid.Column width={4}>
                <FormSelectList
                  label="Financial Term Unit"
                  name="financialTermUnit"
                  dropDownOptions={FinancialTermUnitTypes}
                  defaultValue={
                    usePercentages ? TermUnits.PERCENTAGE : TermUnits.USD
                  }
                  onSelectChange={changeFinancialUnit}
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <FormInput
                  name="cashback"
                  type="number"
                  step="any"
                  placeholder="Enter Cash Back"
                  label={`Cash Back (${usePercentages ? '%' : '$'})`}
                  register={register}
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <FormInput
                  name="cashbackDailyLimit"
                  type="number"
                  placeholder="Enter a Daily Cashback Limit"
                  label="Daily Cashback Limit ($)"
                  register={register}
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <FormInput
                  name="marketingFee"
                  step="any"
                  type="number"
                  placeholder="Enter Marketing Fee for Campaign"
                  label={`Marketing Fee (${usePercentages ? '%' : '$'})`}
                  register={register}
                />
              </Grid.Column>
            </Grid.Row>

            <Grid.Row className={classes.sectionHeader}>
              <hr className={classes.divider} />
              <h3>Visibility</h3>
            </Grid.Row>

            <Grid.Row>
              <Grid.Column width={4}>
                <FormSelectList
                  label="Segment Type"
                  name="segmentType"
                  dropDownOptions={SegmentTypes}
                  defaultValue={SegmentType.TARGETED}
                  onSelectChange={handleSegmentTypeChange}
                />
              </Grid.Column>
              <Grid.Column width={8}>
                <FormSelectList
                  label="Marketplaces"
                  name="marketplaces"
                  dropDownOptions={MarketplaceOptions}
                  defaultValue={baseSegment.marketplaces || []}
                  multiple
                  wrapSelection
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={8}>
                <SelectAudience
                  merchantId={merchantId}
                  disabled={segmentType === SegmentType.MUG}
                  onSelectChange={handleAudienceChange}
                  attachedAudiences={attachedAudiences}
                  filterAttachedAudiences
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <FormInput
                  name="segmentPriority"
                  type="number"
                  placeholder="Enter the Segment Priority"
                  label="Segment Priority"
                  register={register}
                />
              </Grid.Column>
            </Grid.Row>
            {segmentType === SegmentType.MUG && (
              <Grid.Row>
                <Grid.Column width={8}>
                  <FormInput
                    name="mugId"
                    type="text"
                    placeholder="Enter the MUG UUID"
                    label="MUG UUID"
                    register={register}
                  />
                </Grid.Column>
              </Grid.Row>
            )}

            <Grid.Row className={classes.sectionHeader}>
              <hr className={classes.divider} />
              <h3>Throttle</h3>
            </Grid.Row>

            <Grid.Row>
              <Grid.Column width={4}>
                <FormSelectList
                  label="Attach Throttle"
                  name="useThrottle"
                  dropDownOptions={ThrottleOptions}
                  disabled={hideOfferForSegment}
                  onSelectChange={handleUseThrottleChange}
                />
              </Grid.Column>

              {useThrottle === 'existing' && (
                <>
                  <Grid.Column width={12}>
                    <FormSelectList
                      label="Existing Throttles"
                      name="existingThrottle"
                      dropDownOptions={existingThrottleOptions}
                      onSelectChange={handleThrottleChange}
                    />
                  </Grid.Column>
                </>
              )}
              {useThrottle === 'create' && (
                <>
                  <Grid.Column width={12}>
                    <Slider
                      name="throttleRange"
                      type="range"
                      placeholder=""
                      label={`Visible to Audience - ${throttleRange(
                        currentMax ?? attachedThrottles[0].max,
                        currentMin ?? attachedThrottles[0].min,
                      )}%`}
                      step="1"
                      throttle={attachedThrottles[0]}
                    />
                  </Grid.Column>
                  <Grid.Column width={16}>
                    <FormInput
                      name="name"
                      type="text"
                      placeholder="Enter a name for this throttle"
                      label="Name"
                      register={register}
                    />
                  </Grid.Column>
                  <Grid.Column width={16}>
                    <FormInput
                      name="description"
                      type="text"
                      placeholder="Brief description for this throttle"
                      label="Description"
                      register={register}
                    />
                  </Grid.Column>
                </>
              )}
            </Grid.Row>

            <Grid.Row className={classes.formRow}>
              <button
                className={classnames(
                  classes.button,
                  classes.buttonSecondary,
                  classes.mediumBtn,
                )}
                onClick={handleClick}
              >
                Cancel
              </button>
              <Button
                type="submit"
                disabled={
                  !formState.isValid || isSubmitting || !budgetId || !payerType
                }
                className={classnames(classes.button, classes.mediumBtn)}
              >
                {isSubmitting ? '...Submitting' : 'Create Offer Segment'}
              </Button>
            </Grid.Row>
          </Grid>
        </form>
      </FormProvider>
    </Container>
  );
};

export default withStyles(styles)(AddSegment);
